import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { FaTwitter, FaInstagram, FaTiktok, FaYoutube, FaDiscord } from 'react-icons/fa';
import { HiPaperAirplane } from 'react-icons/hi2';
import Button from './ui/Button';

const socialLinks = [
  { name: 'Twitter', icon: FaTwitter, url: 'https://twitter.com/YOUR_HANDLE', color: '#1DA1F2' },
  { name: 'Instagram', icon: FaInstagram, url: 'https://instagram.com/YOUR_HANDLE', color: '#E4405F' },
  { name: 'TikTok', icon: FaTiktok, url: 'https://tiktok.com/@YOUR_HANDLE', color: '#00f2ea' },
  { name: 'YouTube', icon: FaYoutube, url: 'https://www.youtube.com/@WhiteRabbitBangers', color: '#FF0000' },
  { name: 'Discord', icon: FaDiscord, url: 'https://discord.gg/YOUR_SERVER', color: '#5865F2' },
];

export default function Contact() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle newsletter signup - integrate with your email service
    console.log('Newsletter signup:', email);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
    setEmail('');
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-cyber-dark overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] rounded-full opacity-10"
          style={{
            background: 'radial-gradient(ellipse, rgba(0, 212, 255, 0.5) 0%, transparent 70%)',
          }}
        />
      </div>

      {/* Animated lines */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute h-px bg-gradient-to-r from-transparent via-cyber-cyan/30 to-transparent"
            style={{ top: `${20 + i * 15}%`, width: '100%' }}
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 8,
              delay: i * 0.5,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        ))}
      </div>

      {/* Epic Action Mascot - Left Side */}
      <motion.div
        className="absolute left-0 bottom-0 w-[300px] h-[400px] md:w-[400px] md:h-[500px] lg:w-[450px] lg:h-[550px] pointer-events-none hidden md:block"
        initial={{ opacity: 0, x: -100 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 1.2, delay: 0.5, ease: 'easeOut' }}
      >
        {/* Glow effect behind mascot */}
        <motion.div
          className="absolute inset-0 blur-3xl"
          animate={{
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ duration: 4, repeat: Infinity }}
          style={{
            background: 'radial-gradient(circle at 70% 50%, rgba(139, 92, 246, 0.4) 0%, transparent 60%)',
          }}
        />
        
        {/* Character */}
        <motion.div
          className="relative w-full h-full"
          animate={{
            y: [0, -15, 0],
            rotate: [0, 1, 0, -1, 0],
          }}
          transition={{
            y: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
            rotate: { duration: 6, repeat: Infinity, ease: 'easeInOut' },
          }}
        >
          <img
            src="/images/mascot-action.png"
            alt="White Rabbit Action"
            className="object-contain object-bottom drop-shadow-[0_0_40px_rgba(139,92,246,0.5)] w-full h-full"
          />
        </motion.div>
        
        {/* Energy particles around mascot - deterministic positions to avoid hydration mismatch */}
        {[
          { left: 35, top: 25, color: '#00d4ff', duration: 3.0, xOffset: 12 },
          { left: 55, top: 35, color: '#8b5cf6', duration: 2.8, xOffset: -8 },
          { left: 45, top: 50, color: '#00d4ff', duration: 3.5, xOffset: 10 },
          { left: 65, top: 40, color: '#8b5cf6', duration: 2.6, xOffset: -15 },
          { left: 40, top: 65, color: '#00d4ff', duration: 3.2, xOffset: 8 },
          { left: 60, top: 55, color: '#8b5cf6', duration: 2.9, xOffset: -10 },
          { left: 50, top: 30, color: '#00d4ff', duration: 3.8, xOffset: 14 },
          { left: 70, top: 70, color: '#8b5cf6', duration: 2.7, xOffset: -6 },
        ].map((particle, i) => (
          <motion.div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              background: particle.color,
            }}
            animate={{
              y: [0, -40, 0],
              x: [0, particle.xOffset, 0],
              opacity: [0, 1, 0],
              scale: [0, 1.5, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: i * 0.4,
            }}
          />
        ))}

        {/* Lightning bolt accents */}
        <motion.div
          className="absolute right-[20%] top-[20%] w-px h-20 bg-gradient-to-b from-white/80 via-cyber-purple/50 to-transparent"
          animate={{
            opacity: [0, 1, 0],
            scaleY: [0, 1, 0],
          }}
          transition={{
            duration: 0.3,
            repeat: Infinity,
            repeatDelay: 3,
          }}
        />
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto px-4" ref={ref}>
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-orbitron text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-white">STAY </span>
            <span className="bg-gradient-to-r from-cyber-cyan via-cyber-purple to-cyber-pink bg-clip-text text-transparent">
              CONNECTED
            </span>
          </h2>
          <p className="text-white/60 font-rajdhani text-lg max-w-2xl mx-auto">
            You take the blue pill—the story ends. You take the red pill—you stay in Wonderland 
            and get exclusive updates, early releases, and behind-the-scenes content.
          </p>
        </motion.div>

        {/* Newsletter Form */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <div className="flex-1 relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-6 py-4 bg-cyber-darker/80 border border-cyber-cyan/30 rounded-xl 
                         text-white font-rajdhani text-lg placeholder:text-white/40
                         focus:outline-none focus:border-cyber-cyan focus:shadow-[0_0_20px_rgba(0,212,255,0.3)]
                         transition-all duration-300"
              />
              {/* Electric line animation */}
              <motion.div
                className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-cyber-cyan to-cyber-purple"
                initial={{ width: '0%' }}
                whileHover={{ width: '100%' }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <Button
              variant="primary"
              size="lg"
              className="whitespace-nowrap"
              icon={!isSubmitted ? <HiPaperAirplane className="w-5 h-5" /> : undefined}
            >
              {isSubmitted ? '✓ Subscribed!' : 'Subscribe'}
            </Button>
          </form>
          <p className="text-center text-white/40 text-sm mt-4 font-rajdhani">
            No spam. Unsubscribe anytime. We respect your privacy.
          </p>
        </motion.div>

        {/* Social Links */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <p className="text-white/50 text-sm font-rajdhani mb-6 uppercase tracking-widest">
            🐇 Follow The White Rabbit
          </p>
          <div className="flex justify-center gap-4">
            {socialLinks.map((social, index) => (
              <motion.a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-4 glass-card rounded-xl transition-all duration-300"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.5 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.1,
                  borderColor: social.color,
                }}
                style={{
                  ['--social-color' as string]: social.color,
                }}
              >
                <social.icon 
                  className="text-2xl text-white/70 group-hover:text-[var(--social-color)] transition-colors"
                />
                {/* Glow effect on hover */}
                <motion.div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity -z-10"
                  style={{
                    background: `radial-gradient(circle, ${social.color}20 0%, transparent 70%)`,
                  }}
                />
                {/* Tooltip */}
                <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-xs font-rajdhani text-white/60 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {social.name}
                </span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Business Inquiries */}
        <motion.div
          className="text-center mt-16 glass-card rounded-2xl p-8"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.8 }}
        >
          <h3 className="font-orbitron text-xl font-bold text-white mb-2">
            Business Inquiries
          </h3>
          <p className="text-white/60 font-rajdhani mb-4">
            For collaborations, licensing, and booking requests:
          </p>
          <a
            href="mailto:contact@whiterabbitbangers.com"
            className="inline-block font-mono text-cyber-cyan hover:text-cyber-purple transition-colors"
          >
            contact@whiterabbitbangers.com
          </a>
        </motion.div>
      </div>
    </section>
  );
}

